import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchBanners, 
  createBanner, 
  updateBanner, 
  deleteBanner,
  selectAllBanners,
  selectBannersStatus,
  selectBannersError
} from '../../data/bannerSlice';

const Banners = () => {
  const dispatch = useDispatch();
  const banners = useSelector(selectAllBanners);
  const status = useSelector(selectBannersStatus);
  const error = useSelector(selectBannersError);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null
  });
  const [previewImage, setPreviewImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  // Load banners on component mount
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchBanners());
    }
  }, [status, dispatch]);

  // Handle opening the banner form modal
  const handleOpenModal = (banner = null) => {
    if (banner) {
      setCurrentBanner(banner);
      setFormData({
        title: banner.title || '',
        description: banner.description || '',
        image: null
      });
      setPreviewImage(banner.image || '');
    } else {
      setCurrentBanner(null);
      setFormData({
        title: '',
        description: '',
        image: null
      });
      setPreviewImage('');
    }
    setErrorMessage('');
    setIsModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle image file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file
      });
      
      // Create preview URL for the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      // Validate form data
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }

      // Create or update banner
      if (currentBanner) {
        // Update existing banner
        await dispatch(updateBanner({ 
          bannerId: currentBanner.id, 
          bannerData: formData 
        })).unwrap();
      } else {
        // Create new banner
        if (!formData.image) {
          throw new Error('Image is required');
        }
        await dispatch(createBanner(formData)).unwrap();
      }

      // Close modal and reset form
      setIsModalOpen(false);
      setFormData({ title: '', description: '', image: null });
      setPreviewImage('');
      setCurrentBanner(null);
    } catch (err) {
      setErrorMessage(err.message || 'An error occurred while saving the banner');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle banner deletion
  const handleDeleteBanner = (banner) => {
    setCurrentBanner(banner);
    setIsDeleteModalOpen(true);
  };

  // Confirm banner deletion
  const confirmDelete = async () => {
    if (currentBanner) {
      setIsLoading(true);
      try {
        await dispatch(deleteBanner(currentBanner.id)).unwrap();
        setIsDeleteModalOpen(false);
        setCurrentBanner(null);
      } catch (err) {
        setErrorMessage('Failed to delete banner');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Format image URL for display
  const getImageUrl = (image) => {
    if (!image) return '/assets/image1.jpeg';
    if (typeof image === 'string') {
      // Handle base64 encoded image data from API
      if (image.startsWith('data:image')) {
        return image;
      }
      // Handle URLs
      return image.startsWith('http') ? image : `/assets/${image}`;
    }
    return '/assets/image1.jpeg';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Banner Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Banner
        </button>
      </div>

      {/* Status indicators */}
      {status === 'loading' && !isLoading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {status === 'failed' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error || 'Failed to load banners'}</p>
        </div>
      )}

      {/* Banner List */}
      {status === 'succeeded' && (
        <>
          {banners.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No banners found</h3>
              <p className="mt-1 text-gray-500">Get started by creating a new banner.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {banners.map((banner) => (
                <div key={banner.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <div className="relative h-48">
                    <img 
                      src={getImageUrl(banner.image)} 
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{banner.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{banner.description || 'No description provided'}</p>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(banner)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteBanner(banner)}
                        className="bg-red-100 hover:bg-red-200 text-red-800 font-medium py-2 px-4 rounded-lg"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Banner Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {currentBanner ? 'Edit Banner' : 'Add New Banner'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="px-6 py-4">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{errorMessage}</p>
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="title">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Banner Image {!currentBanner && <span className="text-red-500">*</span>}
                </label>
                
                {previewImage && (
                  <div className="mb-3">
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                )}
                
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
                  >
                    {currentBanner ? 'Change Image' : 'Select Image'}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center"
                >
                  {isLoading && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {currentBanner ? 'Update Banner' : 'Create Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Delete Banner</h3>
              <p className="text-gray-600">
                Are you sure you want to delete the banner "{currentBanner?.title}"? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center"
              >
                {isLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Delete Banner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;
